import React, { ReactNode, createContext, useContext } from 'react';
import { ActiveTimer, useActiveTimer } from '../hooks';
import { Issue } from '../types';

interface TimerContextType {
  activeTimer: ActiveTimer | null;
  elapsedTime: number;
  startTimer: (issue: Issue) => void;
  clearTimer: () => void;
}

const TimerContext = createContext<TimerContextType | null>(null);

export const useTimer = (): TimerContextType => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

interface TimerProviderProps {
  children: ReactNode;
}

/**
 * Separate context for timer state (activeTimer, elapsedTime).
 * elapsedTime changes every second — isolating it prevents re-rendering
 * the entire component tree that consumes TimeTrackerContext.
 */
export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  const { activeTimer, elapsedTime, startTimer, clearTimer } = useActiveTimer();

  return (
    <TimerContext.Provider value={{ activeTimer, elapsedTime, startTimer, clearTimer }}>
      {children}
    </TimerContext.Provider>
  );
};
